import { pipelineCapacityDisplay } from '../lib/pipeline-capacity';
import React, { useState, useEffect, useMemo } from 'react';
import { designTokens, getDCScoreColor, getDCScoreTier } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import {
  fetchCountyDetail,
  type CountyScore, type CountyDetailParcel, type CountyDetailPipeline, type CountyDetailListing,
} from '../lib/api';

const C = designTokens.colors;
const T = designTokens.typography;

const fmtNum = (n: number | null | undefined) =>
  n != null ? Math.round(Number(n)).toLocaleString() : '-';

const fmtPct = (n: number | null | undefined) =>
  n != null ? `${Number(n).toFixed(1)}%` : '-';

const fmtMiles = (n: number | null | undefined) =>
  n != null ? `${Number(n).toFixed(1)} mi` : '-';

const getCompositeColor = (score: number): string => {
  if (score >= 70) return C.dcPrime;
  if (score >= 50) return C.dcStrong;
  if (score >= 30) return C.dcViable;
  return C.dcWeak;
};

const printStyles = `
@media print {
  .county-detail-toolbar { display: none !important; }
  @page { margin: 0.4in; }
  .county-detail-section { page-break-inside: avoid; }
}
`;

interface CountyDetailProps {
  state: string;
  county: string;
  rank?: number;
  compositeScore?: number;
  onClose: () => void;
  onViewOnMap?: (bbox: [number, number, number, number]) => void;
}

export const CountyDetail: React.FC<CountyDetailProps> = ({
  state, county, rank, compositeScore, onClose, onViewOnMap,
}) => {
  const { theme } = useTheme();
  const [score, setScore] = useState<CountyScore | null>(null);
  const [parcels, setParcels] = useState<CountyDetailParcel[]>([]);
  const [pipelines, setPipelines] = useState<CountyDetailPipeline[]>([]);
  const [listings, setListings] = useState<CountyDetailListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    let cancelled = false;
    fetchCountyDetail(state, county)
      .then(data => {
        if (!cancelled) {
          setScore(data.score);
          setParcels(data.parcels);
          setPipelines(data.pipelines);
          setListings(data.listings);
        }
      })
      .catch(() => { if (!cancelled) setError('Failed to load county detail'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [state, county]);

  const tc = useMemo(() => ({
    background: theme.background,
    backgroundSecondary: theme.backgroundSecondary,
    surface: theme.surface,
    text: theme.text,
    textSecondary: theme.textSecondary,
    textTertiary: theme.textTertiary,
    border: theme.border,
    borderLight: theme.borderLight,
  }), [theme]);

  const sectionStyle: React.CSSProperties = { marginBottom: '24px' };

  const sectionHeader = (title: string) => (
    <div style={{
      fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.06em', color: C.chartPrimary, marginBottom: '8px',
      borderBottom: `1px solid ${tc.border}`, paddingBottom: '4px',
    }}>
      {title}
    </div>
  );

  const thStyle: React.CSSProperties = {
    padding: '6px 8px', textAlign: 'left', fontWeight: 600,
    fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.06em',
    color: tc.textSecondary, background: tc.surface,
  };

  const hasBbox = score && score.bbox_west != null && score.bbox_south != null && score.bbox_east != null && score.bbox_north != null;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw',
      fontFamily: T.fontFamily.display, background: tc.background, color: tc.text,
    }}>
      <style>{printStyles}</style>

      {/* Toolbar */}
      <div className="county-detail-toolbar" style={{
        height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', background: tc.backgroundSecondary, borderBottom: `1px solid ${tc.border}`, flexShrink: 0,
      }}>
        <button onClick={onClose} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', color: tc.textSecondary,
          cursor: 'pointer', fontSize: '12px', fontFamily: T.fontFamily.display,
        }}>
          &larr; Back to Counties
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasBbox && onViewOnMap && (
            <button
              onClick={() => onViewOnMap([
                Number(score!.bbox_west), Number(score!.bbox_south),
                Number(score!.bbox_east), Number(score!.bbox_north),
              ])}
              style={{
                padding: '4px 14px', background: 'none', color: tc.textSecondary,
                border: `1px solid ${tc.border}`, borderRadius: '2px', fontSize: '11px',
                fontWeight: 500, cursor: 'pointer', fontFamily: T.fontFamily.display,
              }}
            >
              View on Map
            </button>
          )}
          <button onClick={() => window.print()} style={{
            padding: '4px 14px', background: C.chartPrimary, color: '#E4E6E8',
            border: 'none', borderRadius: '2px', fontSize: '11px', fontWeight: 600,
            cursor: 'pointer', fontFamily: T.fontFamily.display,
          }}>
            Print
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div className="county-detail-document" style={{ maxWidth: '1000px', margin: '0 auto', padding: '32px 24px 64px' }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: tc.textTertiary }}>
              Loading county detail...
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: C.error }}>{error}</div>
          )}

          {score && !loading && (
            <>
              {/* Header */}
              <div className="county-detail-section" style={sectionStyle}>
                <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: tc.textTertiary }}>County Detail</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: C.chartPrimary, lineHeight: 1.3 }}>
                    {county}, {state}
                  </div>
                  {compositeScore != null && (
                    <span style={{
                      padding: '2px 8px', borderRadius: '2px', fontSize: '11px', fontWeight: 600,
                      background: getCompositeColor(compositeScore), color: '#E4E6E8',
                    }}>
                      {compositeScore}/100
                      {rank != null && ` (#${rank})`}
                    </span>
                  )}
                  {!score.has_parcel_data && (
                    <span style={{
                      padding: '2px 6px', fontSize: '9px', fontWeight: 600,
                      background: C.warningLight, color: C.warning, borderRadius: '2px',
                    }}>PARTIAL DATA</span>
                  )}
                </div>
              </div>

              {/* Score Summary Cards */}
              <div className="county-detail-section" style={sectionStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  <ScoreCard label="Gas Infrastructure" score={score.gas_infrastructure_score} details={[
                    `${score.transmission_count} transmission + ${score.gathering_count} gathering`,
                    `${score.unique_operator_count} operators`,
                  ]} />
                  <ScoreCard label="Land Availability" score={score.land_availability_score} details={[
                    `${fmtNum(score.suitable_parcel_count)} suitable parcels`,
                    `${fmtNum(score.total_suitable_acreage)} suitable acres`,
                    `${score.active_listing_count} active listings`,
                    score.avg_listing_price_per_acre != null ? `$${fmtNum(score.avg_listing_price_per_acre)}/acre avg` : null,
                  ]} />
                  <ScoreCard label="Acquisition Signals" score={score.acquisition_signals_score} details={[
                    `${fmtPct(score.absentee_owner_pct)} absentee owners`,
                    `${fmtNum(score.entity_owner_count)} entity owners`,
                    `${fmtNum(score.tax_delinquent_count)} tax delinquent`,
                    `${fmtNum(score.portfolio_holder_count)} portfolio holders`,
                  ]} />
                  <ScoreCard label="Site Quality" score={score.site_quality_score} details={[
                    `${score.prime_site_count} PRIME + ${score.strong_site_count} STRONG`,
                    score.avg_dc_suitability_score != null ? `${Number(score.avg_dc_suitability_score).toFixed(0)} avg DC score` : null,
                    `${fmtPct(score.fiber_available_pct)} fiber, ${fmtPct(score.water_service_pct)} water`,
                    `${fmtMiles(score.avg_substation_miles)} avg to substation`,
                  ]} />
                </div>
              </div>

              {/* Top Parcels */}
              {parcels.length > 0 && (
                <div className="county-detail-section" style={sectionStyle}>
                  {sectionHeader(`Top ${parcels.length} Parcels by DC Suitability`)}
                  <p style={{ fontSize: '12px', lineHeight: 1.5, color: tc.textSecondary }}>
                    Gas delivery is unverified. Scores retain the existing screening method while the gas model is recalibrated.
                  </p>
                  <div style={{ background: tc.backgroundSecondary, border: `1px solid ${tc.border}`, borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFeatureSettings: '"tnum"', minWidth: '700px' }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
                            <th style={{ ...thStyle, textAlign: 'center', width: '30px' }}>#</th>
                            <th style={thStyle}>Owner</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Acres</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>DC Score</th>
                            <th style={thStyle}>Land Use</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Infra</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parcels.map((p, i) => {
                            const dcScore = p.dc_suitability_score != null ? Number(p.dc_suitability_score) : null;
                            return (
                              <tr key={p.id} style={{
                                borderBottom: i < parcels.length - 1 ? `1px solid ${tc.borderLight}` : 'none',
                                background: i % 2 === 1 ? C.brandSubtle : 'transparent',
                              }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: tc.textTertiary, fontFamily: T.fontFamily.mono, fontSize: '10px' }}>{i + 1}</td>
                                <td style={{ padding: '5px 8px', fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.owner_name}
                                  {p.is_absentee && <span style={{ marginLeft: '4px', fontSize: '8px', color: tc.textTertiary }}>ABS</span>}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: T.fontFamily.mono }}>{p.acreage != null ? Number(p.acreage).toFixed(0) : '-'}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  {dcScore != null ? (
                                    <span style={{
                                      display: 'inline-block', padding: '1px 6px', borderRadius: '2px',
                                      fontSize: '9px', fontWeight: 600,
                                      background: getDCScoreColor(dcScore), color: '#E4E6E8',
                                    }}>
                                      {getDCScoreTier(dcScore)} {dcScore}
                                    </span>
                                  ) : <span style={{ color: tc.textTertiary }}>Unassessed</span>}
                                </td>
                                <td style={{ padding: '5px 8px', color: tc.textSecondary, fontSize: '10px' }}>{p.land_use_category || '-'}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '10px', fontFamily: T.fontFamily.mono }}>
                                  <span style={{ color: p.fiber_available ? C.success : C.chartQuaternary }} title="Fiber">{p.fiber_available ? 'F' : '-'}</span>
                                  {' '}
                                  <span style={{ color: p.water_service_area ? C.success : C.chartQuaternary }} title="Water">{p.water_service_area ? 'W' : '-'}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Gas Infrastructure */}
              {pipelines.length > 0 && (
                <div className="county-detail-section" style={sectionStyle}>
                  {sectionHeader(`Gas Infrastructure (${pipelines.length} pipelines)`)}
                  <div style={{ background: tc.backgroundSecondary, border: `1px solid ${tc.border}`, borderRadius: '2px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFeatureSettings: '"tnum"' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
                          <th style={thStyle}>Name</th>
                          <th style={thStyle}>Operator</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Type</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Published capacity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pipelines.map((p, i) => (
                          <tr key={p.id} title={`${pipelineCapacityDisplay(p).label}: ${pipelineCapacityDisplay(p).explanation}`} style={{
                            borderBottom: i < pipelines.length - 1 ? `1px solid ${tc.borderLight}` : 'none',
                            background: i % 2 === 1 ? C.brandSubtle : 'transparent',
                          }}>
                            <td style={{ padding: '5px 8px', fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || '-'}</td>
                            <td style={{ padding: '5px 8px', color: tc.textSecondary }}>{p.operator || '-'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                              <span style={{
                                padding: '1px 5px', borderRadius: '2px', fontSize: '9px', fontWeight: 600,
                                background: p.type === 'gas_transmission' ? C.pipelineGasTransmission : C.pipelineGathering,
                                color: '#E4E6E8',
                              }}>
                                {p.type === 'gas_transmission' ? 'TRANS' : 'GATH'}
                              </span>
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: T.fontFamily.mono }}>{pipelineCapacityDisplay(p).capacityMmcf != null ? `${pipelineCapacityDisplay(p).capacityMmcf!.toFixed(0)} MMcf/d` : 'Unconfirmed'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Land Listings */}
              {listings.length > 0 && (
                <div className="county-detail-section" style={sectionStyle}>
                  {sectionHeader(`Land Listings (${listings.length})`)}
                  <div style={{ background: tc.backgroundSecondary, border: `1px solid ${tc.border}`, borderRadius: '2px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFeatureSettings: '"tnum"' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
                          <th style={thStyle}>Listing</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Price</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Acres</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>$/Acre</th>
                          <th style={thStyle}>Seller</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listings.map((l, i) => (
                          <tr key={l.id} style={{
                            borderBottom: i < listings.length - 1 ? `1px solid ${tc.borderLight}` : 'none',
                            background: i % 2 === 1 ? C.brandSubtle : 'transparent',
                          }}>
                            <td style={{ padding: '5px 8px', fontWeight: 500, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {l.listing_url ? (
                                <a href={l.listing_url} target="_blank" rel="noopener noreferrer" style={{ color: C.chartPrimary, textDecoration: 'none' }}>
                                  {l.listing_title || 'View Listing'}
                                </a>
                              ) : (l.listing_title || '-')}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: T.fontFamily.mono }}>
                              {l.price != null ? `$${fmtNum(l.price)}` : '-'}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: T.fontFamily.mono }}>{l.acreage != null ? Number(l.acreage).toFixed(0) : '-'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: T.fontFamily.mono }}>{l.price_per_acre != null ? `$${fmtNum(l.price_per_acre)}` : '-'}</td>
                            <td style={{ padding: '5px 8px', color: tc.textSecondary }}>{l.seller_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Infrastructure Inventory */}
              {score.has_parcel_data && (
                <div className="county-detail-section" style={sectionStyle}>
                  {sectionHeader('Infrastructure Inventory')}
                  <div style={{ background: tc.backgroundSecondary, border: `1px solid ${tc.border}`, borderRadius: '2px', padding: '12px 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                      <StatRow label="Fiber Coverage" value={fmtPct(score.fiber_available_pct)} />
                      <StatRow label="Water Service" value={fmtPct(score.water_service_pct)} />
                      <StatRow label="Avg Substation Dist" value={fmtMiles(score.avg_substation_miles)} />
                      <StatRow label="Avg Highway Dist" value={fmtMiles(score.avg_highway_miles)} />
                    </div>
                  </div>
                </div>
              )}

              {/* Acquisition Signals */}
              {score.has_parcel_data && (
                <div className="county-detail-section" style={sectionStyle}>
                  {sectionHeader('Acquisition Signals')}
                  <div style={{ background: tc.backgroundSecondary, border: `1px solid ${tc.border}`, borderRadius: '2px', padding: '12px 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                      <StatRow label="Absentee Owner Rate" value={fmtPct(score.absentee_owner_pct)} />
                      <StatRow label="Entity Owners" value={fmtNum(score.entity_owner_count)} />
                      <StatRow label="Tax Delinquent" value={fmtNum(score.tax_delinquent_count)} />
                      <StatRow label="Portfolio Holders" value={fmtNum(score.portfolio_holder_count)} />
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{
                textAlign: 'center', color: tc.textTertiary, fontSize: '10px',
                borderTop: `1px solid ${tc.border}`, paddingTop: '16px',
              }}>
                {county}, {state} County Detail &middot; {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &middot; Confidential
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* --- Sub-components --- */

const ScoreCard: React.FC<{
  label: string; score: number; details: (string | null)[];
}> = ({ label, score, details }) => {
  const { theme } = useTheme();
  const color = getCompositeColor(score);
  return (
    <div style={{
      background: theme.backgroundSecondary, border: `1px solid ${theme.border}`,
      borderTop: `3px solid ${color}`, borderRadius: '2px', padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
        <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.textSecondary }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: T.fontFamily.mono, fontFeatureSettings: '"tnum"', color, lineHeight: 1 }}>{score}</div>
      </div>
      {details.filter(Boolean).map((d, i) => (
        <div key={i} style={{ fontSize: '10px', color: theme.textTertiary, lineHeight: 1.6 }}>{d}</div>
      ))}
    </div>
  );
};

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '2px 0' }}>
      <span style={{ color: theme.textSecondary }}>{label}</span>
      <span style={{ fontWeight: 600, fontFamily: T.fontFamily.mono, color: theme.text }}>{value}</span>
    </div>
  );
};
